import React from "react";
import { useSelector } from "react-redux";

import { Preloader, SeatItem } from "../../../../components";
import { hallsSelector } from "../../../../redux/halls/selectors";
import { setSelectedHalls } from "../../../../redux/halls/slice";
import { cartSelector } from "../../../../redux/cart/selectors";

import { ISelectedHall, ITime } from "../../../../redux/halls/types";
import { useAppDispatch } from "../../../../redux/store";
import { setSelectedHallsToStorage } from "../../../../utils/storage/halls";

import styles from "./TimesTable.module.scss";

export const TimesTable: React.FC = () => {
  const { date, lastSelectedHall, hallTimes, isLoading } = useSelector(hallsSelector);
  const { basket } = useSelector(cartSelector);
  const dispatch = useAppDispatch();

  const isSlotInBasket = React.useCallback((slotTime: string, hallId: number | undefined) => {
    if (!hallId || !basket?.seats) return false;
    
    return Object.values(basket.seats).some((item) => {
      if ('hallId' in item && 'time' in item && 'date' in item) {
        const hallItem = item as ISelectedHall;
        return (
          hallItem.hallId === hallId &&
          hallItem.date === date &&
          hallItem.time === slotTime
        );
      }
      return false;
    });
  }, [basket, date]);

  function chooseHallTime(newSelectedHalls: { [key: string]: ISelectedHall }) {
    dispatch(setSelectedHalls(newSelectedHalls));
    setSelectedHallsToStorage(newSelectedHalls);
  }

  function seatTimesUp(seatTime: string) {
    const currentDay = new Date(Date.parse(date)).getDate();
    const nowDay = new Date().getDate();
    if (currentDay !== nowDay) return false;

    const seatHour = +seatTime.substring(0, 2).replace(":", "");

    const seatDate = new Date();
    seatDate.setHours(seatHour, 0, 0, 0);

    return seatDate < new Date();
  }

  const seats = React.useMemo(() => {
    return hallTimes.map((time) => {
      const timesUpThisSeat = seatTimesUp(time.title);
      const unAvailableThisSeat = time.busy === true && !time.busyWithYou;
      const isInBasket = isSlotInBasket(time.title, lastSelectedHall?.id);
      const busyWithYou = time.busyWithYou || isInBasket;

      return (
        <div key={time.id}>
          <SeatItem
            data={{
              date: date,
              id: lastSelectedHall?.id,
              time: time.title,
              hallTitle: lastSelectedHall?.title,
              price: time.price,
              startTime: time.startTime,
              finishTime: time.finishTime,
            }}
            onClickFunc={chooseHallTime}
            disabled={timesUpThisSeat || unAvailableThisSeat}
            busyWithYou={busyWithYou}
          />
        </div>
      );
    });
  }, [hallTimes, date, lastSelectedHall, isSlotInBasket]);

  return (
    <div className={styles.wrapper}>
      {isLoading ? <Preloader /> : <div className={styles.table}>{seats}</div>}
    </div>
  );
};
